/**
 * /boards — invisible router that hops the user onto a real board.
 *
 * Behaviour:
 *   1. Fetch the workspace's boards.
 *   2. If at least one exists → navigate to the first (newest) one.
 *   3. If the list is empty AND the role can create → auto-provision a
 *      starter board so the founder always lands on real Kanban content,
 *      never on a "no boards yet" screen.
 *   4. Roles that can't create (auditor / external) stay on the page
 *      and see a quiet empty-state message — they need an admin to
 *      create their first board.
 *
 * Founder mandate (2026-05-15, Notion-style refactor): no boards-landing
 * grid; the sidebar is the only place that lists / switches boards.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { kanbanApi, kanbanKeys } from '../../lib/api/kanban';
import { useAuthStore } from '../../stores/auth-store';

const STARTER_BOARD_NAME = 'My first board';

function BoardsIndexRedirect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const role = useAuthStore(
    (s) => s.workspaces.find((w) => w.workspace.id === s.activeWorkspaceId)?.membership.role,
  );
  const canCreate = role !== undefined && role !== 'auditor' && role !== 'external';

  const boardsQ = useQuery({
    queryKey: kanbanKeys.boards(),
    queryFn: () => kanbanApi.listBoards(),
  });

  const createM = useMutation({
    mutationFn: (name: string) => kanbanApi.createBoard({ name }),
    onSuccess: async (board) => {
      await qc.invalidateQueries({ queryKey: kanbanKeys.boards() });
      await navigate({ to: '/board/$boardId', params: { boardId: board.id } });
    },
  });

  const decidedRef = React.useRef(false);
  React.useEffect(() => {
    if (decidedRef.current) return;
    if (!boardsQ.data) return;

    if (boardsQ.data.length > 0) {
      decidedRef.current = true;
      const first = boardsQ.data[0];
      if (first) {
        void navigate({ to: '/board/$boardId', params: { boardId: first.id } });
      }
      return;
    }

    if (canCreate && !createM.isPending && !createM.isSuccess) {
      decidedRef.current = true;
      createM.mutate(STARTER_BOARD_NAME);
    }
  }, [boardsQ.data, canCreate, createM, navigate]);

  return (
    <div className="flex h-full items-center justify-center px-8 text-center">
      {boardsQ.isLoading || createM.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !canCreate && boardsQ.data && boardsQ.data.length === 0 ? (
        <p className="max-w-md text-sm text-muted-foreground">{t('kanban.boards.empty_state')}</p>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/boards')({
  component: BoardsIndexRedirect,
});
